import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { PageStrings } from '@alltrails/shared/utils/pageStringHelpers';
import { usersContentPrivacyPolicy, userCanEdit } from 'utils/user_helpers';
import { arrayOf, shape, object, string, func } from 'prop-types';
import { VISIBILITY_PUBLIC, CUSTOM_MAPS } from '../../../utils/privacy_policy_helpers';
import { routePropTypes } from '../../../proptypes/shared';
import Accordion from '../../shared/Accordion';
import * as styles from './styles/styles.module.scss';
import Button from '../../shared/Button';
import CreatedMapModal from '../CreatedMapModal';
import TrailResultCard from '../../cards/TrailResultCard';
import RouteSection from '../Routing/RouteSection';
import { useRoutes, CANCEL_ROUTE_FORM, UPDATE_FOR_INCOMING_ROUTES } from '../../../hooks/useRoutes';
import { useWaypoints, UPDATE_FOR_INCOMING_WAYPOINTS, CANCEL_WAYPOINT_FORM } from '../../../hooks/useWaypoints';
import WaypointsSection from '../../Waypoints/WaypointsSection';
import DescriptionForm from '../DescriptionForm';
import ActionHeader from '../../shared/ActionHeader';
import { activities, defaultHikingActivity } from '../../../utils/activity_util';
import { routeRequest, waypointRequest, formatWaypointForMap } from '../../../utils/map_creator_util';
import EditHeader from '../EditHeader';
import { deleteMap } from '../../../utils/requests/at_map_requests';
import PhotoSection from '../Photos';
import DownloadRouteModal from '../../DownloadRouteModal';
import { modalSignup } from '../../../utils/modal_funnel_util';
import { StringConstantsMixin } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import useEntityTranslation from '../../../hooks/useEntityTranslation';
import { logIndividualContentPrivacyChangedAmplitudeEvent } from '../../../utils/privacy_policy_amplitude_helpers';

const { LINKED_TRAIL, EDIT_MAP, DESCRIPTION, WAYPOINTS, PHOTOS, SAVE_CUSTOM_MAP } = StringConstantsMixin.strings.MAP_CREATOR;

const MapCreatorPanel = ({
  map,
  context,
  messagingChannel,
  mapboxAccessToken,
  handleMapUpdate,
  openTrailFormModal,
  updateMapPhotos,
  openShareModal,
  resultCardFunctions,
  linkedTrail,
  initialIsEditing,
  updateExploreMap,
  saveMapChanges,
  routeInfo,
  updateProgressDialog,
  showMapMobile,
  hidePanel,
  listMethods,
  handleRouteInfoChanged,
  saveMapAsClone
}) => {
  // editingMethods
  let saveMap;
  let handleRouteSuccess;
  let handleRouteSave;
  let saveAndDisplayMap;
  let createMapWithWaypoint;
  let handleSuccessfulMapSave;
  let handleMapError;

  const canEdit = userCanEdit(context, map.user);
  const [isEditing, toggleEditing] = useState(initialIsEditing);
  const [showDownloadRouteModal, setShowDownloadRouteModal] = useState(false);
  const [showCreatedMapModal, toggleCreatedMapModal] = useState(false);
  const [submittingState, updateSubmittingState] = useState({ fromSubForm: false, isSubmitting: false, isNew: initialIsEditing });
  const [routingState, dispatchRoutes] = useRoutes({ initialRoutes: map.routes, messagingChannel, routeInfo, canEdit });
  const [waypointsState, dispatchWaypoints] = useWaypoints({ initialWaypoints: map.waypoints, messagingChannel, canEdit });
  const prevStateRef = useRef({ isEditing, showCreatedMapModal, submittingState, routingState, waypointsState, map });
  const isCreateMapView = new URLSearchParams(window.location.search).has('save');

  const initialAccordionStates = {
    description: !initialIsEditing,
    waypoints: map.waypoints.length > 0,
    photos: map.mapPhotos.length > 0,
    linkedTrail: false
  };

  const isMapOwner = context.currentUser?.id === map.user?.id;
  const { translation: description } = useEntityTranslation(map, 'description', isMapOwner);

  const defaultContentPrivacy = usersContentPrivacyPolicy(context, CUSTOM_MAPS);

  const setInitialContentPrivacy = () => {
    if (map && map.contentPrivacy) {
      return map.contentPrivacy;
    }

    return defaultContentPrivacy || VISIBILITY_PUBLIC;
  };

  const initialDescriptionState = {
    title: map ? map.name : '',
    description,
    activity: map.activity ? activities.find(a => a.value === map.activity.uid) : defaultHikingActivity(),
    date: map.created_at ? new Date(map.created_at) : new Date(),
    isPrivate: map ? !!map.private : false,
    contentPrivacy: setInitialContentPrivacy()
  };

  const [descriptionChangeset, updateDescriptionChangeset] = useState(initialDescriptionState);
  const [previousDescriptionChangeset, updatePreviousDescriptionChangeset] = useState(initialDescriptionState); // Used for analytics
  const [accordionsOpened, updateAccordionState] = useState(initialAccordionStates);

  const beginEditing = useCallback(() => {
    updatePreviousDescriptionChangeset(descriptionChangeset);
    toggleEditing(true);
  }, [descriptionChangeset]);

  useEffect(() => {
    if (
      (prevStateRef.current.routingState.editIndex === null && routingState.editIndex !== null) ||
      (prevStateRef.current.waypointsState.editIndex === null && waypointsState.editIndex !== null)
    ) {
      beginEditing();
    }
  }, [routingState.editIndex, waypointsState.editIndex, beginEditing]);

  useEffect(() => {
    // keeps previous state tracked
    prevStateRef.current = { isEditing, showCreatedMapModal, submittingState, routingState, waypointsState };
  });

  const handleFindLocationSelect = useCallback(feature => {
    const { latitude, longitude, placeName } = feature;

    // triggers showPopupAtLatLong in TrailPlannerMixin to update map lat/lng location and show popup
    messagingChannel.publish({
      topic: 'edit.show_popup_at_lat_lng',
      data: [latitude, longitude, placeName, 14]
    });
  }, []);

  const updateMap = useCallback(() => {
    if (map && map.id) {
      handleMapUpdate({ id: map.id });
    }
  }, [map]);

  const closeCreatedMapModal = () => {
    toggleCreatedMapModal(false);
  };

  // ROUTE PERSISTENCE
  if (isEditing || isCreateMapView) {
    handleSuccessfulMapSave = responseMap => {
      if (prevStateRef.current.submittingState.isSubmitting && !prevStateRef.current.submittingState.fromSubForm) {
        toggleEditing(false);
        const url = `/explore/map/${responseMap.slug}`;
        window.history.pushState(
          { page: PageStrings.EXPLORE_USERS_TRACKS_MAP_PAGE, selectedObjectId: responseMap.ID, filters: null },
          `Explore ${responseMap.name} | AllTrails`,
          url
        );
      }
      // update the description changeset -- needed because titles created automatically are not reflected in changeset
      if (responseMap.name !== descriptionChangeset.name) {
        updateDescriptionChangeset({ ...descriptionChangeset, title: responseMap.name });
      }
      if (prevStateRef.current.routingState.editParams) {
        dispatchRoutes({ type: CANCEL_ROUTE_FORM });
      }
      if (prevStateRef.current.waypointsState.editParams) {
        dispatchWaypoints({ type: CANCEL_WAYPOINT_FORM });
      }
      if (prevStateRef.current.submittingState.isNew && !prevStateRef.current.submittingState.fromSubForm) {
        toggleCreatedMapModal(true);
        // update so modal doesn't pop up if a user saves twice
        updateSubmittingState(prevState => ({ ...prevState, isNew: false }));
      }

      dispatchRoutes({ type: UPDATE_FOR_INCOMING_ROUTES, payload: responseMap.routes });
      dispatchWaypoints({ type: UPDATE_FOR_INCOMING_WAYPOINTS, payload: responseMap.waypoints });
      updateSubmittingState(prevState => ({ ...prevState, fromSubForm: false, isSubmitting: false }));
    };

    saveMap = (additionalParams = {}) => {
      // This saves map changes via search_app
      saveMapChanges({ descriptionChangeset, ...additionalParams })
        .then(handleSuccessfulMapSave)
        .catch(() => {
          updateSubmittingState(prevState => ({ ...prevState, fromSubForm: false, isSubmitting: false }));
        });
    };

    handleRouteSuccess = newMap => {
      dispatchRoutes({ type: CANCEL_ROUTE_FORM });
      dispatchRoutes({ type: UPDATE_FOR_INCOMING_ROUTES, payload: newMap.routes });
      updateProgressDialog('saved');
      updateExploreMap(newMap);
      updateSubmittingState(prevState => ({ ...prevState, isSubmitting: false }));
    };

    handleMapError = () => {
      updateProgressDialog('error');
      updateSubmittingState(prevState => ({ ...prevState, fromSubForm: false, isSubmitting: false }));
    };

    handleRouteSave = (e = null) => {
      if (e) {
        e.preventDefault();
      }

      if (typeof map.id === 'undefined') {
        updateSubmittingState(prevState => ({ ...prevState, fromSubForm: true, isSubmitting: true }));
        saveMap();
        return;
      }

      if (!routingState.editParams) {
        return;
      }

      updateProgressDialog('saving');
      updateSubmittingState(prevState => ({ ...prevState, isSubmitting: true }));

      routeRequest(map.id, routingState.editParams)
        .then(handleRouteSuccess)
        .catch(handleMapError);
    };

    const saveMapContentsThenMap = () => {
      // if there is an open waypoint or route form
      updateProgressDialog('saving');
      updateSubmittingState(prevState => ({ ...prevState, isSubmitting: true }));
      // if there are not route or waypoint editparams the promises will return null
      Promise.all([routeRequest(map.id, routingState.editParams), waypointRequest(map.id, waypointsState.editParams)])
        .then(() => {
          handleRouteInfoChanged(null);
          saveMap();
        })
        .catch(err => {
          handleMapError(err);
        });
    };

    saveAndDisplayMap = (e, fromSubForm = false) => {
      e.preventDefault();

      logIndividualContentPrivacyChangedAmplitudeEvent(
        CUSTOM_MAPS,
        map.id,
        defaultContentPrivacy,
        previousDescriptionChangeset.contentPrivacy,
        descriptionChangeset.contentPrivacy
      );

      updateSubmittingState(prevState => ({ ...prevState, fromSubForm, isSubmitting: true }));
      if (map.id && (routingState.editParams || waypointsState.editParams)) {
        saveMapContentsThenMap();
        return;
      }

      if (typeof map.id === 'undefined' && waypointsState.editParams) {
        const waypoint = formatWaypointForMap(waypointsState.editParams);
        createMapWithWaypoint(waypoint, false);
        return;
      }

      saveMap();
    };

    createMapWithWaypoint = (waypoint, fromSubForm = true) => {
      if (map.id) {
        return;
      }

      updateSubmittingState(prevState => ({ ...prevState, fromSubForm, isSubmitting: true }));
      const waypoints = [waypoint];
      saveMap({ waypoints });
    };
  }

  const toggleAccordionState = accordionName => {
    updateAccordionState(prevState => ({ ...prevState, [accordionName]: !prevState[accordionName] }));
  };

  const handleShareClick = () => {
    if (!context.currentUser) {
      modalSignup('explore-atmap', window.location.pathname);
      return;
    }

    if (showCreatedMapModal) {
      closeCreatedMapModal();
    }

    openShareModal({
      type: map.presentationType,
      ugc: true,
      id: map.id,
      slug: map.slug,
      name: map.name,
      primaryKey: 'id',
      private: map.private
    });
  };

  const handleDirectionsClick = () => {
    if (!context.currentUser) {
      modalSignup('explore-atmap', window.location.pathname);
      return;
    }

    const {
      location: { latitude, longitude }
    } = map;

    window.open(`https://www.google.com/maps/dir/Current+Location/${latitude},${longitude}`);
  };

  const handlePrintClick = () => {
    // fires handlePrintClick in search_map
    messagingChannel.publish({
      topic: 'print.navigate'
    });
  };

  const openDownloadModal = () => {
    if (!context.currentUser) {
      modalSignup('explore-atmap', window.location.pathname);
    }

    setShowDownloadRouteModal(true);
  };

  const toggleDownloadModal = () => {
    setShowDownloadRouteModal(!showDownloadRouteModal);
  };

  const handleDeleteSuccess = () => {
    window.location.href = LanguageSupportUtil.wrapUrlSafe(`/members/${context.currentUser.slug}/maps`, context.locale);
  };

  const handleDeleteError = () => {
    updateProgressDialog('error-deleting-map');
  };

  const handleDeleteClick = () => {
    if (!window.confirm(StringConstantsMixin.strings.DELETE_ITEM_QUESTION)) {
      return;
    }

    deleteMap(map.id)
      .then(resp => {
        if (resp.errors) {
          handleDeleteError();
          return;
        }

        handleDeleteSuccess();
      })
      .catch(() => {
        handleDeleteError();
      });
  };

  const closeDescriptionForm = () => {
    toggleAccordionState('description');
  };

  const renderHeader = () => {
    if (isEditing || isCreateMapView) {
      return <EditHeader />;
    }

    const { forcePhotoId } = resultCardFunctions;
    const { isFavorite, handleFavoriteClick } = listMethods;
    const bannerPhotoId = forcePhotoId(map.presentationType, map.ID);
    return (
      <ActionHeader
        data={map}
        user={map?.user}
        canEdit={canEdit}
        context={context}
        isFavorite={isFavorite(map.presentationType, map.ID)}
        handleFavoriteClick={handleFavoriteClick}
        bannerPhotoId={bannerPhotoId}
        handleDirectionsClick={handleDirectionsClick}
        openDownloadModal={openDownloadModal}
        handleShareClick={handleShareClick}
        handleDeleteClick={handleDeleteClick}
        saveMapAsClone={() => saveMapAsClone(map)}
        type="map"
      />
    );
  };

  const renderLinkedTrail = () => {
    const { type, ID } = linkedTrail;

    const {
      allowClickingCompletedBadge,
      handleTrailClick,
      handleCompletedBadgeClick,
      handleFavoriteClick,
      handleLocationClick
    } = resultCardFunctions;

    const { isComplete, isFavorite, isVerified, showFavoriteHeart } = listMethods;

    const linkedTrailProps = {
      allowClickingCompletedBadge: allowClickingCompletedBadge(type, ID),
      handleCardClick: () => handleTrailClick(linkedTrail),
      handleCompletedBadgeClick: () => handleCompletedBadgeClick(type, ID),
      handleFavoriteClick: () => handleFavoriteClick(type, ID),
      handleLocationClick,
      isComplete: isComplete(type, ID),
      isFavorite: isFavorite(type, ID),
      isVerified: isVerified(type, ID),
      showFavoriteHeart,
      trail: linkedTrail,
      context,
      messagingChannel,
      key: `trail-${linkedTrail.ID}`
    };

    return <TrailResultCard {...linkedTrailProps} />;
  };

  return (
    <>
      <div className={`${styles.panel} ${hidePanel ? styles.hidePanel : ''}`}>
        {renderHeader()}
        <RouteSection
          canEdit={canEdit}
          isEditing={isEditing || isCreateMapView}
          routes={routingState.routes}
          sortMode={routingState.sortMode}
          context={context}
          dispatchRoutes={dispatchRoutes}
          dispatchWaypoints={dispatchWaypoints}
          editIndex={routingState.editIndex}
          editParams={routingState.editParams}
          messagingChannel={messagingChannel}
          summaryStats={map.summaryStats}
          handleFindLocationSelect={handleFindLocationSelect}
          mapboxAccessToken={mapboxAccessToken}
          mapId={map.id}
          updateMap={updateMap}
          openTrailFormModal={openTrailFormModal}
          handleRouteSave={handleRouteSave}
          isSubmitting={submittingState.isSubmitting}
          updateExploreMap={updateExploreMap}
          hidePanel={showMapMobile}
        />
        <div className={styles.accordionContainer}>
          {isEditing || isCreateMapView ? (
            <Accordion onClick={() => toggleAccordionState('description')} isOpen={accordionsOpened.description} title={DESCRIPTION}>
              <DescriptionForm
                initialChangeset={initialDescriptionState}
                changeset={descriptionChangeset}
                updateChangeset={updateDescriptionChangeset}
                handleDescriptionSave={saveAndDisplayMap}
                closeDescriptionForm={closeDescriptionForm}
                locale={context.languageRegionCode}
              />
            </Accordion>
          ) : null}
          <Accordion onClick={() => toggleAccordionState('waypoints')} isOpen={accordionsOpened.waypoints} title={WAYPOINTS}>
            <WaypointsSection
              {...waypointsState}
              dispatchFormState
              dispatchWaypoints={dispatchWaypoints}
              messagingChannel={messagingChannel}
              mapId={map.id}
              canEdit={canEdit}
              isAdmin={context.adminUser}
              createMapWithWaypoint={createMapWithWaypoint}
            />
          </Accordion>
          {map && map.id && (
            <Accordion onClick={() => toggleAccordionState('photos')} isOpen={accordionsOpened.photos} title={PHOTOS}>
              <PhotoSection
                canEdit={canEdit}
                mapId={map.id}
                mapPhotos={map.mapPhotos}
                updateMapPhotos={updateMapPhotos}
                openTrailFormModal={openTrailFormModal}
                isEditing={isEditing || isCreateMapView}
              />
            </Accordion>
          )}
          {linkedTrail && (
            <Accordion onClick={() => toggleAccordionState('linkedTrail')} isOpen={accordionsOpened.linkedTrail} title={LINKED_TRAIL}>
              {renderLinkedTrail()}
            </Accordion>
          )}
        </div>
        {context.currentUser &&
          showDownloadRouteModal &&
          ReactDOM.createPortal(
            <DownloadRouteModal toggleDownloadModal={toggleDownloadModal} mapId={map.id} />,
            document.getElementById('modalPortal')
          )}
        <div className={styles.panelSpacer} />
        {canEdit ? (
          <div className={styles.panelBottom}>
            {isEditing || isCreateMapView ? (
              <Button onClick={saveAndDisplayMap} variant="primary" disabled={submittingState.isSubmitting}>
                {SAVE_CUSTOM_MAP}
              </Button>
            ) : (
              <Button onClick={beginEditing} variant="secondary">
                {EDIT_MAP}
              </Button>
            )}
          </div>
        ) : (
          <div className={styles.panelJib} />
        )}
        {showCreatedMapModal &&
          ReactDOM.createPortal(
            <CreatedMapModal
              closeModal={closeCreatedMapModal}
              handleShareClick={handleShareClick}
              handleDirectionsClick={handleDirectionsClick}
              handleDownloadRoute={openDownloadModal}
              handlePrintClick={handlePrintClick}
            />,
            document.getElementById('modalPortal')
          )}
      </div>
    </>
  );
};

MapCreatorPanel.propTypes = {
  map: shape({
    routes: arrayOf(routePropTypes)
  }),
  openTrailFormModal: func,
  messagingChannel: object,
  mapboxAccessToken: string,
  handleMapUpdate: func,
  resultCardFunctions: object
};

MapCreatorPanel.defaultProps = {
  map: {},
  handleMapUpdate: () => {},
  openTrailFormModal: () => {},
  messagingChannel: {},
  mapboxAccessToken: '',
  resultCardFunctions: {}
};

export default MapCreatorPanel;
